// The audio maths, ported from the phone-verified prototype
// (ConvoAgent websocket_client/table.html @ cab5ea1). The wire format is raw
// signed 16-bit little-endian PCM at 16 kHz mono, no header and no envelope, in
// both directions — anything else on the socket is a bug.
//
// These are the only place this maths exists. Get one of them subtly wrong and
// nothing throws: the agent just mishears, or a sentence clips.

/** The rate on the wire. Hard-coded server-side too; not negotiable. */
export const TARGET_RATE = 16000

/**
 * Linear interpolation down to the target rate. No anti-alias filter — the
 * prototype shipped without one and a phone's AudioContext is almost always
 * 48 kHz, an exact 3:1 ratio where it barely matters.
 *
 * Each call restarts the source index at 0, so there is no phase carried
 * between blocks. At 48→16 that is exact; at an odd rate like 44.1 kHz it puts
 * a small discontinuity at each block edge, which the prototype accepted.
 */
export function resample(input: Float32Array, inRate: number, outRate: number): Float32Array {
  if (inRate === outRate) return input
  const ratio = inRate / outRate
  const out = new Float32Array(Math.round(input.length / ratio))
  for (let i = 0; i < out.length; i++) {
    const src = i * ratio
    const lo = Math.floor(src)
    // Clamped, so the last output sample degenerates to sample-and-hold rather
    // than reading past the end.
    const hi = Math.min(lo + 1, input.length - 1)
    out[i] = input[lo] + (input[hi] - input[lo]) * (src - lo)
  }
  return out
}

/**
 * Float [-1, 1] to signed 16-bit.
 *
 * The scaling is asymmetric on purpose: two's complement has one more negative
 * value than positive, so -1 maps to -32768 and +1 to 32767. Using 0x8000 for
 * both would wrap a full-scale positive sample to a full-scale negative one —
 * an audible click on the loudest syllable, and nothing would report it.
 */
export function floatToPCM16(f32: Float32Array): Int16Array {
  const out = new Int16Array(f32.length)
  for (let i = 0; i < f32.length; i++) {
    const s = Math.max(-1, Math.min(1, f32[i]))
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff
  }
  return out
}

/**
 * Signed 16-bit back to float, dividing by 0x8000 for both signs.
 *
 * Deliberately not the mirror of floatToPCM16: 32767/32768 is off by one part
 * in 32768, which is inaudible, and matching the capture path's asymmetry here
 * would buy nothing. The prototype does it this way.
 */
export function pcm16ToFloat(pcm: Int16Array): Float32Array {
  const out = new Float32Array(pcm.length)
  for (let i = 0; i < pcm.length; i++) out[i] = pcm[i] / 0x8000
  return out
}

/**
 * When to schedule the next chunk of playback.
 *
 * Audio scheduled in the past is not delayed, it is cut. The lead is a jitter
 * budget, paid once at the start of each bot turn: without it a single late
 * chunk silently clips the end of a sentence. Never schedule at `currentTime`.
 */
export function nextStart(now: number, head: number, leadSecs: number): number {
  return head > now ? head : now + leadSecs
}

/** Loudness of a block, for driving the orb. */
export function rms(buf: Float32Array): number {
  let sum = 0
  for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i]
  return Math.sqrt(sum / buf.length)
}

/**
 * Fast attack, slow release. Symmetric smoothing makes the orb strobe on every
 * consonant; this lets it jump to a syllable and fall away gently.
 */
export function smooth(prev: number, target: number): number {
  return prev + (target - prev) * (target > prev ? 0.35 : 0.08)
}
